import { useState, useMemo } from 'react';
import {
  ArrowUpDown, ArrowUp, ArrowDown, Trash2,
  Settings2, Download, Copy, FilterX, RefreshCw,
  ChevronsUpDown, Check, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { Card, CardContent, CardHeader, CardDescription } from '@/components/ui/card';
import { ItemStatusBadge } from '../shared/StatusBadge';
import { SafeColorDiv } from '../shared/SafeColorDiv';
import { SupplierInfiniteCombobox } from '../suppliers/SupplierInfiniteCombobox';
import { buildLocationCode } from '../../utils/validation';
import type { InventoryItem, ItemStatus } from '../../types';

// =============================================================================
// Column Definition
// =============================================================================

interface ColumnDef {
  key: string;
  label: string;
  defaultVisible?: boolean;
  sortKey?: string;
  filterType?: 'text' | 'select' | 'searchable-select' | 'number-range';
  filterOptions?: { label: string; value: string }[];
  getValue: (item: InventoryItem) => string | number | null;
  render?: (item: InventoryItem) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
  noFilter?: boolean;
  className?: string;
}

const STATUS_OPTIONS: { label: string; value: ItemStatus }[] = [
  { label: 'Valid', value: 'valid' },
  { label: 'Verify', value: 'verify' },
  { label: 'Test', value: 'test' },
  { label: 'Dev', value: 'dev' },
  { label: 'Dead', value: 'dead' },
];

const COLUMNS: ColumnDef[] = [
  {
    key: 'sku_code',
    label: 'SKU',
    sortKey: 'sku_code',
    filterType: 'text',
    getValue: (item) => item.sku_code,
    render: (item) => <span className="font-mono">{item.sku_code}</span>,
  },
  {
    key: 'sku_name',
    label: 'Name',
    sortKey: 'sku_name',
    filterType: 'text',
    getValue: (item) => item.sku_name || item.sku_details?.name?.en || '',
    render: (item) => (
      <span className="max-w-[200px] truncate block">
        {item.sku_name || item.sku_details?.name?.en || '\u2014'}
      </span>
    ),
  },
  {
    key: 'location',
    label: 'Location',
    filterType: 'text',
    getValue: (item) => item.full_location_code || buildLocationCode(item.zone_code, item.bin_code),
    render: (item) => (
      <div className="flex items-center gap-2">
        <SafeColorDiv
          color={item.zone_color}
          className="w-2.5 h-2.5 rounded-full shrink-0"
        />
        <span className="font-mono">
          {item.full_location_code || buildLocationCode(item.zone_code, item.bin_code)}
        </span>
      </div>
    ),
  },
  {
    key: 'supplier',
    label: 'Supplier',
    filterType: 'text',
    defaultVisible: false,
    getValue: (item) => item.preferred_supplier_name || '',
    render: (item) => (
      <span className="max-w-[150px] truncate block text-muted-foreground">
        {item.preferred_supplier_name || '\u2014'}
      </span>
    ),
  },
  {
    key: 'quantity_on_hand',
    label: 'On Hand',
    sortKey: 'quantity_on_hand',
    filterType: 'number-range',
    getValue: (item) => item.quantity_on_hand,
    render: (item) => <span className="font-medium">{item.quantity_on_hand}</span>,
    align: 'right',
  },
  {
    key: 'unit_of_measure',
    label: 'Unit',
    filterType: 'text',
    defaultVisible: false,
    getValue: (item) => item.unit_of_measure || '',
    render: (item) => (
      <span className="text-muted-foreground">{item.unit_of_measure || '\u2014'}</span>
    ),
  },
  {
    key: 'quantity_reserved',
    label: 'Reserved',
    sortKey: 'quantity_reserved',
    filterType: 'number-range',
    defaultVisible: false,
    getValue: (item) => item.quantity_reserved || 0,
    render: (item) => (
      <span className="text-muted-foreground">{item.quantity_reserved || 0}</span>
    ),
    align: 'right',
  },
  {
    key: 'quantity_available',
    label: 'Available',
    sortKey: 'quantity_available',
    filterType: 'number-range',
    getValue: (item) => item.quantity_available,
    render: (item) => (
      <>
        <span className={item.is_low_stock ? 'text-orange-500 font-medium' : ''}>
          {item.quantity_available}
        </span>
        {item.is_low_stock && (
          <Badge variant="outline" className="ml-2 text-xs text-orange-500 border-orange-500">
            Low
          </Badge>
        )}
      </>
    ),
    align: 'right',
  },
  {
    key: 'item_status',
    label: 'Status',
    filterType: 'select',
    filterOptions: STATUS_OPTIONS,
    getValue: (item) => item.item_status,
    render: (item) => <ItemStatusBadge status={item.item_status} />,
  },
  {
    key: 'actions',
    label: '',
    noFilter: true,
    getValue: () => '',
    align: 'right',
  },
];

// =============================================================================
// Column Visibility
// =============================================================================

const VISIBILITY_STORAGE_KEY = 'atek-wms-inventory-column-visibility';

function getDefaultVisibility(): Record<string, boolean> {
  const defaults: Record<string, boolean> = {};
  for (const col of COLUMNS) {
    defaults[col.key] = col.defaultVisible !== false;
  }
  return defaults;
}

function loadColumnVisibility(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(VISIBILITY_STORAGE_KEY);
    if (stored) return { ...getDefaultVisibility(), ...JSON.parse(stored) };
  } catch { /* ignore */ }
  return getDefaultVisibility();
}

function saveColumnVisibility(visibility: Record<string, boolean>) {
  localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(visibility));
}

// =============================================================================
// SortableHeader
// =============================================================================

function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
}: {
  label: string;
  sortKey: string;
  currentSortKey?: string;
  sortDirection?: 1 | -1;
  onSort: (key: string) => void;
}) {
  const isActive = currentSortKey === sortKey;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 hover:bg-transparent"
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive ? (
        sortDirection === 1 ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

// =============================================================================
// SearchableSelectFilter (for future use with large option sets)
// =============================================================================

function SearchableSelectFilter({
  colKey,
  options,
  value,
  onChange,
}: {
  colKey: string;
  options: { label: string; value: string }[];
  value: string;
  onChange: (key: string, value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected: string[] = useMemo(() => {
    try { return value ? JSON.parse(value) : []; }
    catch { return []; }
  }, [value]);

  const filtered = useMemo(() => {
    const base = search
      ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
      : options;
    if (selected.length === 0) return base;
    const sel = new Set(selected);
    return [...base].sort((a, b) => {
      const aS = sel.has(a.value) ? 0 : 1;
      const bS = sel.has(b.value) ? 0 : 1;
      return aS - bS;
    });
  }, [options, search, selected]);

  const toggle = (opt: string) => {
    const next = selected.includes(opt)
      ? selected.filter((o) => o !== opt)
      : [...selected, opt];
    onChange(colKey, next.length ? JSON.stringify(next) : '');
  };

  const selectedLabel = useMemo(() => {
    if (selected.length === 0) return null;
    if (selected.length === 1) {
      const match = options.find((o) => o.value === selected[0]);
      return match?.label || '1 selected';
    }
    return `${selected.length} selected`;
  }, [selected, options]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative">
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-7 w-full text-xs justify-between font-normal">
            <span className="truncate">{selectedLabel || '\u2014'}</span>
            <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        {selected.length > 0 && (
          <button
            type="button"
            onClick={() => { onChange(colKey, ''); setOpen(false); }}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 z-10"
          >
            <X className="h-3 w-3 opacity-50 hover:opacity-100" />
          </button>
        )}
      </div>
      <PopoverContent className="w-56 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search..." value={search} onValueChange={setSearch} className="h-8 text-xs" />
          <CommandList>
            <CommandEmpty className="py-2 text-center text-xs text-muted-foreground">No results</CommandEmpty>
            <CommandGroup className="max-h-48 overflow-y-auto">
              {filtered.map((opt) => (
                <CommandItem key={opt.value} value={opt.value} onSelect={() => toggle(opt.value)} className="text-xs">
                  <div className={cn('mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                    selected.includes(opt.value) ? 'bg-primary text-primary-foreground' : 'opacity-50')}>
                    {selected.includes(opt.value) && <Check className="h-3 w-3" />}
                  </div>
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// =============================================================================
// Filter Row Rendering
// =============================================================================

function renderColumnFilter(
  col: ColumnDef,
  columnFilters: Record<string, string>,
  setColumnFilter: (key: string, value: string) => void,
  selectedSupplierId?: string,
  onSelectedSupplierChange?: (supplierId: string) => void,
  searchableSelectOptions?: Record<string, { label: string; value: string }[]>,
) {
  if (col.noFilter) return <TableHead key={`filter-${col.key}`} />;

  if (col.key === 'supplier' && onSelectedSupplierChange) {
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <SupplierInfiniteCombobox
          value={selectedSupplierId}
          onChange={onSelectedSupplierChange}
          placeholder="All Suppliers"
        />
      </TableHead>
    );
  }

  if (col.filterType === 'searchable-select') {
    const options = searchableSelectOptions?.[col.key] || [];
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <SearchableSelectFilter
          colKey={col.key}
          options={options}
          value={columnFilters[col.key] || ''}
          onChange={setColumnFilter}
        />
      </TableHead>
    );
  }

  if (col.filterType === 'select' && col.filterOptions) {
    const selected: string[] = (() => {
      try { return columnFilters[col.key] ? JSON.parse(columnFilters[col.key]) : []; }
      catch { return []; }
    })();

    const toggle = (opt: string) => {
      const next = selected.includes(opt)
        ? selected.filter((o) => o !== opt)
        : [...selected, opt];
      setColumnFilter(col.key, next.length ? JSON.stringify(next) : '');
    };

    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 w-full text-xs justify-start font-normal">
              {selected.length ? `${selected.length} selected` : '\u2014'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            {col.filterOptions.map((opt) => (
              <DropdownMenuCheckboxItem
                key={opt.value}
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
              >
                {opt.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableHead>
    );
  }

  if (col.filterType === 'number-range') {
    const raw = columnFilters[col.key] || '';
    const [min = '', max = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={min}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${max}`)}
            placeholder="Min"
          />
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={max}
            onChange={(e) => setColumnFilter(col.key, `${min}>>${e.target.value}`)}
            placeholder="Max"
          />
        </div>
      </TableHead>
    );
  }

  // Text filter (default)
  return (
    <TableHead key={`filter-${col.key}`} className="p-1">
      <Input
        className="h-7 text-xs"
        value={columnFilters[col.key] || ''}
        onChange={(e) => setColumnFilter(col.key, e.target.value)}
        placeholder="..."
      />
    </TableHead>
  );
}

// =============================================================================
// CSV Export
// =============================================================================

function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  let str = String(value);
  if (/^[=+\-@\t\r]/.test(str)) str = "'" + str;
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function exportToCSV(items: InventoryItem[], columns: ColumnDef[], filename: string) {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => escapeCSVValue(c.label)).join(',');
  const rows = items.map((item) =>
    exportCols.map((c) => escapeCSVValue(c.getValue(item))).join(','),
  );
  const csvContent = [header, ...rows].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function copyToClipboard(items: InventoryItem[], columns: ColumnDef[]) {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => c.label).join('\t');
  const rows = items.map((item) =>
    exportCols.map((c) => String(c.getValue(item) ?? '')).join('\t'),
  );
  const text = [header, ...rows].join('\n');
  return navigator.clipboard.writeText(text);
}

// =============================================================================
// InventoryTable Component
// =============================================================================

interface InventoryTableProps {
  items: InventoryItem[];
  total: number;
  isLoading: boolean;
  // Column filters (client-side refinement)
  columnFilters: Record<string, string>;
  onColumnFilterChange: (key: string, value: string) => void;
  onClearColumnFilters: () => void;
  selectedSupplierId?: string;
  onSelectedSupplierChange?: (supplierId: string) => void;
  // Sort (server-side)
  sortKey?: string;
  sortDirection?: 1 | -1;
  onSort: (key: string) => void;
  // Pagination (server-side)
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  // Actions
  onEdit: (item: InventoryItem) => void;
  onDelete: (item: InventoryItem) => void;
  // Refresh
  onRefresh: () => void;
  isRefreshing: boolean;
  // Export all
  onExportAll?: () => void;
  isExportingAll?: boolean;
}

export function InventoryTable({
  items,
  total,
  isLoading,
  columnFilters,
  onColumnFilterChange,
  onClearColumnFilters,
  selectedSupplierId,
  onSelectedSupplierChange,
  sortKey,
  sortDirection,
  onSort,
  page,
  totalPages,
  onPageChange,
  onEdit,
  onDelete,
  onRefresh,
  isRefreshing,
  onExportAll,
  isExportingAll,
}: InventoryTableProps) {
  // Column visibility
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(loadColumnVisibility);

  const toggleColumn = (key: string) => {
    const updated = { ...columnVisibility, [key]: !columnVisibility[key] };
    setColumnVisibility(updated);
    saveColumnVisibility(updated);
  };

  const isVisible = (key: string) => columnVisibility[key] !== false;
  const visibleColumns = COLUMNS.filter((col) => isVisible(col.key));

  // Client-side column filter refinement within page
  const filteredItems = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(([, v]) => v);
    if (activeFilters.length === 0) return items;

    return items.filter((item) =>
      activeFilters.every(([colKey, filterValue]) => {
        const col = COLUMNS.find((c) => c.key === colKey);
        if (!col || col.noFilter) return true;

        if (col.key === 'supplier') {
          return !selectedSupplierId || item.preferred_supplier_id === selectedSupplierId;
        }

        const rawValue = col.getValue(item);

        // Multi-select
        if (col.filterType === 'select' || col.filterType === 'searchable-select') {
          try {
            const selected = JSON.parse(filterValue) as string[];
            return selected.length === 0 || selected.includes(String(rawValue || ''));
          } catch { return true; }
        }

        // Number range: "min>>max"
        if (col.filterType === 'number-range') {
          const [minStr, maxStr] = filterValue.split('>>');
          const num = Number(rawValue);
          if (isNaN(num)) return false;
          if (minStr && num < Number(minStr)) return false;
          if (maxStr && num > Number(maxStr)) return false;
          return true;
        }

        // Text: case-insensitive includes
        return String(rawValue ?? '').toLowerCase().includes(filterValue.toLowerCase());
      }),
    );
  }, [items, columnFilters, selectedSupplierId]);

  const hasActiveColumnFilters = Object.values(columnFilters).some(Boolean);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardDescription>
            {filteredItems.length < items.length
              ? `Showing ${filteredItems.length} of ${items.length} on this page`
              : `Showing ${items.length} of ${total}`}
          </CardDescription>
          <div className="flex items-center gap-2">
            {hasActiveColumnFilters && (
              <Button variant="outline" size="sm" onClick={onClearColumnFilters}>
                <FilterX className="h-4 w-4 mr-1" />
                Clear filters
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                copyToClipboard(filteredItems, COLUMNS);
                toast.success('Copied to clipboard');
              }}
            >
              <Copy className="h-4 w-4 mr-1" />
              Copy
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={isExportingAll}>
                  <Download className="h-4 w-4 mr-1" />
                  {isExportingAll ? 'Exporting...' : 'CSV'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Export</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => {
                  exportToCSV(filteredItems, COLUMNS, 'wms-inventory');
                  toast.success(`Exported ${filteredItems.length} rows`);
                }}>
                  Export Page ({filteredItems.length} rows)
                </DropdownMenuItem>
                {onExportAll && (
                  <DropdownMenuItem onClick={onExportAll}>
                    Export All ({total})
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" size="sm" onClick={onRefresh} disabled={isRefreshing}>
              <RefreshCw className={cn('h-4 w-4 mr-1', isRefreshing && 'animate-spin')} />
              Refresh
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings2 className="h-4 w-4 mr-1" />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {COLUMNS.filter((c) => !c.noFilter).map((col) => (
                  <DropdownMenuCheckboxItem
                    key={col.key}
                    checked={isVisible(col.key)}
                    onCheckedChange={() => toggleColumn(col.key)}
                  >
                    {col.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              {/* Row 1: Filter inputs */}
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                {visibleColumns.map((col) => renderColumnFilter(
                  col,
                  columnFilters,
                  onColumnFilterChange,
                  selectedSupplierId,
                  onSelectedSupplierChange,
                ))}
              </TableRow>

              {/* Row 2: Column labels + sort */}
              <TableRow>
                {visibleColumns.map((col) => (
                  <TableHead key={col.key} className={cn(col.align === 'right' && 'text-right')}>
                    {col.sortKey ? (
                      <SortableHeader
                        label={col.label}
                        sortKey={col.sortKey}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                        onSort={onSort}
                      />
                    ) : (
                      col.label
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {/* Loading */}
              {isLoading && Array.from({ length: 10 }).map((_, i) => (
                <TableRow key={i}>
                  {visibleColumns.map((col) => (
                    <TableCell key={col.key}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))}

              {/* Empty */}
              {!isLoading && filteredItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} className="py-12 text-center text-muted-foreground">
                    No inventory items found
                  </TableCell>
                </TableRow>
              )}

              {/* Data */}
              {!isLoading && filteredItems.map((item) => (
                <TableRow key={item.id} className="cursor-pointer hover:bg-muted/50">
                  {visibleColumns.map((col) => (
                    <TableCell
                      key={col.key}
                      className={cn(
                        'text-sm',
                        col.align === 'right' && 'text-right tabular-nums',
                        col.className,
                      )}
                    >
                      {col.key === 'actions' ? (
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); onEdit(item); }}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={(e) => { e.stopPropagation(); onDelete(item); }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : col.render ? (
                        col.render(item)
                      ) : (
                        String(col.getValue(item) ?? '') || '\u2014'
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t px-4 py-3 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Page {page + 1} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => onPageChange(page - 1)} disabled={page === 0}>
                Previous
              </Button>
              <Button variant="outline" size="sm" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages - 1}>
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
